import { Box, SelectChangeEvent } from '@mui/material';
import { DataGridProProps, GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid-pro';
import { useEffect, useState } from 'react';
import { StyleObj } from '../../@types';
import { DailyReportItem, WalletType } from '../../@types/api';
import { dayjs } from '../../config/dayjs';
import { DATE_FORMAT } from '../../constants';
import { generateDateRangeTimestamps, getTotalsRowClass } from '../../helpers';
import { createNumberColumn, createPercentageColumn } from '../../helpers/columns';
import { usePagination } from '../../hooks/usePagination';
import useSort from '../../hooks/useSort';
import { createColumn } from '../../utils';
import DailyReportsTotalUsers from '../atoms/DailyReportsTotalUsers';
import DateRangeSelect, { DayjsTimestamp } from '../molecules/DateRangeSelect';
import TableTemplate from '../templates/TableTemplate';
import { WALLET_TYPE } from '../../constants/enums';
import API_ROUTES from '../../constants/apiRoutes';
import CollapsibleFilters from '../molecules/CollapsibleFilters';
import WalletTypeSelect from '../molecules/WalletTypeSelect';

type DailyReportRow = DailyReportItem & {
  isTotalsRow?: boolean;
};

const styles: StyleObj = {
  container: {
    position: 'relative',
    height: 'calc(100% - 200px)',
  },
  table: {
    border: 'none',
    '& .MuiDataGrid-columnHeader': {
      border: '1px solid rgba(0, 83, 55, 0.20)',
      backgroundColor: 'background.lightGreen',
      '&:first-of-type': {
        borderRadius: 0,
      },
      '&:last-of-type': {
        borderRadius: 0,
        width: '100% !important',
        maxWidth: 'unset !important',
      },
      '& .MuiDataGrid-sortIcon': {
        color: 'grey.700',
      },
    },
    '& .MuiDataGrid-columnHeadersInner': {
      width: '100%',
      '> div': {
        width: '100%',
      },
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 700,
      color: 'grey.700',
    },
    '& .MuiDataGrid-row:nth-of-type(even)': {
      backgroundColor: 'background.light',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
      },
    },
    '& .MuiDataGrid-columnHeaders:hover .MuiDataGrid-columnSeparator': {
      visibility: 'hidden',
    },
    '& .customGroupHeader': {
      backgroundColor: 'primary.main',
      '& .MuiDataGrid-columnHeaderTitleContainer': {
        justifyContent: 'center',
      },
      '& .MuiDataGrid-columnHeaderTitle': {
        fontWeight: 700,
        color: 'white',
      },
    },
    '& .MuiDataGrid-columnHeader--emptyGroup': {
      backgroundColor: 'primary.main',
    },
    '& .MuiDataGrid-virtualScrollerRenderZone': {
      '& .MuiDataGrid-row--lastVisible': {
        backgroundColor: '#D9E7D9',
        '& .MuiDataGrid-cell': {
          fontWeight: '700',
        },
      },
    },
  },
};

const columnGroupingModel: GridColumnGroupingModel = [
  {
    groupId: 'Users',
    description: 'Users',
    headerClassName: 'customGroupHeader',
    children: [{ field: 'registeredCount' }],
  },
  {
    groupId: 'Deposits',
    description: 'Deposits',
    headerClassName: 'customGroupHeader',
    children: [{ field: 'depositCount' }, { field: 'depositAmount' }],
  },
  {
    groupId: 'Withdrawals',
    description: 'Withdrawals',
    headerClassName: 'customGroupHeader',
    children: [{ field: 'withdrawalCount' }, { field: 'withdrawalAmount' }],
  },
  {
    groupId: 'Sports Betting',
    description: 'Sports Betting',
    headerClassName: 'customGroupHeader',
    children: [
      { field: 'ticketCount' },
      { field: 'stakeAmount' },
      { field: 'ticketWonCount' },
      { field: 'winningAmount' },
      { field: 'jackpotAmount' },
      { field: 'profitAmount' },
      { field: 'profitMarginPercentage' },
    ],
  },
];

const columns: GridColDef[] = [
  createColumn('date', 'Date', {
    valueGetter: (_value, row, _column, _apiRef) =>
      dayjs(row.date).isValid() ? dayjs(row.date).format(DATE_FORMAT) : '',
  }),
  createColumn('registeredCount', 'Registered'),
  createColumn('depositCount', 'Total'),
  createNumberColumn('depositAmount', 'Total Amount'),
  createColumn('withdrawalCount', 'Total'),
  createNumberColumn('withdrawalAmount', 'Total Amount'),
  createColumn('ticketCount', 'Tickets Placed'),
  createNumberColumn('stakeAmount', 'Stake'),
  createColumn('ticketWonCount', 'Tickets Won'),
  createNumberColumn('winningAmount', 'Winnings'),
  createNumberColumn('jackpotAmount', 'Jackpots'),
  createNumberColumn('profitAmount', 'Profit'),
  createPercentageColumn('profitMarginPercentage', 'Profit Margin %'),
];

const generateTotalsRow = (data?: DailyReportItem): DailyReportRow | undefined => {
  if (data) {
    return {
      ...data,
      date: 'Totals',
      isTotalsRow: true,
    };
  }
};

const getMobileCardHeaderItems = (report: DailyReportRow) => [
  {
    value: dayjs(report.date).isValid() ? dayjs(report.date).format(DATE_FORMAT) : '',
    flex: 1,
    align: 'left' as const,
  },
  { label: 'Profit', value: report.profitAmount.toFixed(2) },
  {
    label: 'Margin',
    value: Number(report.profitMarginPercentage).toFixed(2) + '%',
  },
];

const DailyReportsPage = () => {
  const [walletType, setWalletType] = useState<WalletType | null>(WALLET_TYPE.MAIN);
  const [date, setDate] = useState<DayjsTimestamp>({
    fromTimestamp: dayjs().startOf('day'),
    toTimestamp: dayjs().endOf('day'),
  });

  const { fromTimestamp, toTimestamp } = generateDateRangeTimestamps('today');
  const { data, changeQuery, isFetching, isLoading, updateQueryParams } = usePagination<DailyReportItem>(
    API_ROUTES.REPORTS.DAILY,
    {
      page: 1,
      limit: 25,
      fromTimestamp: fromTimestamp.valueOf(),
      toTimestamp: toTimestamp.valueOf(),
      walletType,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }
  );

  const { handleSort } = useSort(changeQuery);

  const handleWalletTypeChange = (e: SelectChangeEvent<WalletType | null>) => {
    setWalletType(e.target.value as WalletType | null);
    changeQuery({ walletType: e.target.value });
  };

  const handleDateChange = (newDate: DayjsTimestamp) => {
    setDate(newDate);
  };

  useEffect(() => {
    changeQuery({
      fromTimestamp: date.fromTimestamp.valueOf(),
      toTimestamp: date.toTimestamp.valueOf(),
    });
  }, [date, changeQuery]);

  const totalsRow = generateTotalsRow(data?.total as unknown as DailyReportItem);
  const aggregatedData: DailyReportRow[] = totalsRow ? [...(data?.items ?? []), totalsRow] : (data?.items ?? []);

  return (
    <Box sx={styles.container}>
      <TableTemplate
        getRowId={(params: DailyReportRow) => params.date}
        columnGroupingModel={columnGroupingModel}
        rows={aggregatedData}
        columns={columns}
        sx={styles.table as DataGridProProps['sx']}
        loading={isFetching || isLoading}
        rowCount={data?.count || 0}
        handlePaginationModelChange={updateQueryParams}
        handleSort={handleSort}
        getRowClassName={getTotalsRowClass}
        changeQuery={changeQuery}
        mobileCardHeaderItems={getMobileCardHeaderItems}
        customTableHeaderContent={
          <CollapsibleFilters>
            <DailyReportsTotalUsers />
            <WalletTypeSelect value={walletType} onChange={handleWalletTypeChange} />
            <DateRangeSelect date={date} onDateChange={handleDateChange} />
          </CollapsibleFilters>
        }
        showExport
        disableFilter
      />
    </Box>
  );
};

export default DailyReportsPage;
